import { useState } from "react";
import { Link } from "react-router-dom";
import { GUIDE_FAQS } from "../data/housemindFaq";

function SectionCard({ icon, title, children }) {
  return (
    <div className="card mb-16 hm-guide-card">
      <div className="hm-guide-card__head">
        <span className="hm-guide-card__icon">{icon}</span>
        <h3>{title}</h3>
      </div>
      <div className="hm-guide-card__body">{children}</div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div className="hm-guide-step">
      <div className="hm-guide-step__num">{n}</div>
      <div className="hm-guide-step__body">{children}</div>
    </div>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="hm-guide-faq-item">
      <button
        onClick={() => setOpen(v => !v)}
        className="hm-guide-faq-item__button"
      >
        <span>{question}</span>
        <span className={open ? "is-open" : ""}>+</span>
      </button>
      {open && (
        <div className="hm-guide-faq-item__answer">
          {answer}
        </div>
      )}
    </div>
  );
}

function MiniCard({ icon, title, children, tone = "cream" }) {
  return (
    <div className={`hm-guide-mini hm-guide-mini--${tone}`}>
      <span className="hm-guide-mini__icon">{icon}</span>
      <div>
        <div className="hm-guide-mini__title">{title}</div>
        <div className="hm-guide-mini__body">{children}</div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "start",    label: "Getting started",     icon: "🚀" },
  { id: "modules",  label: "Household modules",    icon: "🏠" },
  { id: "ai",       label: "Ask AI",               icon: "✦"  },
  { id: "settings", label: "Settings & support",   icon: "⚙️" },
  { id: "plans",    label: "Plans",                icon: "⭐" },
  { id: "faq",      label: "FAQ",                  icon: "💬" },
];

export default function GuidePage() {
  const [tab, setTab] = useState("start");
  const tabIndex = Math.max(0, TABS.findIndex(t => t.id === tab));
  const nextTab = TABS[(tabIndex + 1) % TABS.length];
  const isLastTab = tabIndex === TABS.length - 1;

  const selectTab = id => {
    setTab(id);
    window.requestAnimationFrame(() => {
      document.querySelector(".hm-guide-nav")?.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
    });
  };

  return (
    <div className="hm-guide-page">
      <div className="page-header hm-guide-header">
        <div>
          <h2>Guide & FAQ</h2>
          <p>
            How HouseMind works — household modules, Timeline, the intelligence layer, Family sharing, and settings.
          </p>
        </div>
      </div>

      <div className="hm-guide-nav" aria-label="Guide navigation">
        <div className="hm-guide-nav__head">
          <span>Jump to section</span>
          <strong>{TABS.find(t => t.id === tab)?.label}</strong>
        </div>
        <div className="hm-guide-tabs" role="tablist" aria-label="Guide sections">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`hm-guide-tab${tab === t.id ? " is-active" : ""}`}
              onClick={() => selectTab(t.id)}
            >
              <span aria-hidden="true">{t.icon}</span>
              <strong>{t.label}</strong>
            </button>
          ))}
        </div>
      </div>

      {/* ── GETTING STARTED ── */}
      {tab === "start" && (
        <div>
          <SectionCard icon="🗺️" title="What HouseMind is">
            HouseMind is a household operating system. It connects the details that are easy to forget across food, property,
            home care, pets, safety, money, records, vehicles, subscriptions, utilities, gifts, trips, and events — then surfaces what needs attention before
            it becomes waste, a missed deadline, or an avoidable expense. Every module works on its own, and the dashboard pulls
            them together into one calm command center.
          </SectionCard>

          <SectionCard icon="👣" title="Recommended first setup">
            <Step n={1}>
              Open <Link to="/app/food" className="hm-guide-link">Food</Link> and add pantry items — especially
              anything with an expiration date. Fridge, freezer, pantry, and counter are all tracked separately.
              Use the scan shortcut to speed up entry.
            </Step>
            <Step n={2}>
              Open <Link to="/app/events" className="hm-guide-link">Events & Trips</Link> and add any upcoming
              trips, birthdays, concerts, weddings, or gatherings. Trip dates unlock cross-module alerts like food
              expiring while you're away and subscriptions worth pausing.
            </Step>
            <Step n={3}>
              Open <Link to="/app/people" className="hm-guide-link">People & Gifts</Link> and add the people
              you shop for. Include birthdays, interests, dislikes, budget, and any gift history so HouseMind
              can suggest thoughtful gifts and avoid repeats.
            </Step>
            <Step n={4}>
              Open <Link to="/app/home-vault" className="hm-guide-link">Home Vault</Link> and add appliances,
              warranties, home systems, maintenance tasks, receipts, and manuals. The more you add, the more
              the dashboard can flag before coverage or service windows expire.
            </Step>
            <Step n={5}>
              Add your property basics, one Home Care task, pets, and the parts of your emergency plan your household
              should be able to find quickly. Then add vehicles, subscriptions, utilities, and a monthly budget as needed.
            </Step>
            <Step n={6}>
              Open <Link to="/app/timeline" className="hm-guide-link">Timeline</Link> to review dated activity across
              modules, or use <strong>✶ Ask HouseMind</strong> whenever you want one answer drawn from your household.
            </Step>
          </SectionCard>

          <SectionCard icon="◈" title="Dashboard and daily briefing">
            The dashboard shows four zones. The <strong>Daily Briefing</strong> is an AI paragraph summarizing the
            household that refreshes each morning — it will not say everything is calm if there are urgent items.
            <strong> Action Needed</strong> surfaces specific items across modules sorted by urgency: urgent, soon,
            savings, upcoming, and FYI. <strong>Cross-module Intelligence</strong> appears only when the nightly
            engine finds meaningful connections across modules — for example, a vehicle service due before a road trip,
            or food expiring during a vacation. The <strong>Overview widgets</strong> give a quick read on Food,
            Food, People, Events, Vault, Home Care, Property, Pets, Safety, Vehicles, Subscriptions, Utilities,
            Budget, and your 7-day Timeline.
          </SectionCard>

          <SectionCard icon="🏠" title="Personal account vs Family plan">
            <div className="hm-guide-stack hm-guide-stack--loose">
              <MiniCard icon="🔒" title="Personal workspace">
                Your household data is private to your account by default. Vehicles, subscriptions, utilities,
                and Home Vault are never shared. You control exactly what — if anything — is shared with Family members.
              </MiniCard>
              <MiniCard icon="👥" title="Family plan" tone="green">
                Multiple private logins under one subscription. Family sharing is the collaboration layer.
                Members share your Family AI pool and can only see People &amp; Gifts, Food,
                Events &amp; Trips, and individual pet profiles you explicitly share with them. Nothing is shared automatically.
              </MiniCard>
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── HOUSEHOLD MODULES ── */}
      {tab === "modules" && (
        <div>
          <SectionCard icon="🍽️" title="Food">
            Food has five tabs: <strong>Pantry</strong>, <strong>Meal Plan</strong>, <strong>Recipes</strong>,
            <strong> Shopping List</strong>, and <strong>Preferences</strong>. Pantry tracks fridge, freezer,
            pantry, and counter items with quantities, categories, locations, and expiration dates. Use the scan
            shortcut to add items from a receipt or fridge photo. The meal plan lets you plan the week, mark meals
            as cooked, and log loved or recently-avoided meals so recipe suggestions get smarter over time.
            Shopping list builds automatically from planned meals or manually. Preferences stores allergies,
            dietary type, and dislikes — these act as hard filters for every recipe suggestion and AI food answer.
            <div className="hm-guide-stack">
              <MiniCard icon="⏰" title="Expiration alerts">
                Items expiring within 7 days appear on the dashboard. HouseMind will suggest recipes that
                use them before they go to waste.
              </MiniCard>
              <MiniCard icon="🧾" title="Scan flows">
                Receipt scan and fridge scan let you photograph what you have and bulk-add items to the pantry
                after a quick review step.
              </MiniCard>
              <MiniCard icon="🛒" title="Meal and shopping intelligence">
                Recipes are filtered by your pantry stock, dietary preferences, and allergies. The shopping list
                can be auto-generated from a meal plan or used as a standalone grocery list.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🗂️" title="Home Vault">
            Home Vault stores everything you might need later or need to act on before something expires.
            Record types include <strong>appliances</strong> (fridge, washer, dryer, HVAC, water heater, AC, and
            more), <strong>electronics</strong> (TV, laptop, phone, tablet, game console),
            <strong> home systems</strong> (roof, windows, garage door, security, smoke detectors),
            <strong> maintenance tasks</strong>, <strong>receipts</strong>, <strong>warranties</strong>,
            and <strong>manuals</strong>. Records include model numbers, purchase dates, warranty expiry,
            retailer details, repair notes, and file attachments.
            <div className="hm-guide-stack">
              <MiniCard icon="🏡" title="Owner and renter modes">
                Set your property type in <Link to="/app/property-profile" className="hm-guide-link">Property Profile</Link>.
                Renters unlock a <strong>Lease Advisor</strong> that uses your Vault
                data to help with rent negotiation — it can pull comparable units and draft a negotiation email.
                Owners get home improvement and contractor tracking.
              </MiniCard>
              <MiniCard icon="⏰" title="Warranty and maintenance alerts">
                Expiring warranties and overdue maintenance tasks appear on the dashboard Action Needed queue
                so nothing slips through.
              </MiniCard>
              <MiniCard icon="🔧" title="Maintenance presets">
                Common tasks like AC filter changes, water heater flushes, dryer vent cleaning, and smoke
                detector tests are available as one-tap presets with due-date reminders.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🏡" title="Property Profile">
            Property Profile stores facts about the physical home separately from Home Vault documents and appliances.
            Use it for the address, property type, spaces, access and service details, and residential insurance.
            <div className="hm-guide-stack">
              <MiniCard icon="🛡️" title="Home policy">
                Add policy dates, provider, premium, deductible, coverage, and contacts manually, or scan a declarations
                page and review every extracted field before saving.
              </MiniCard>
              <MiniCard icon="🌤️" title="Shared home context">
                The saved city and state provide the canonical home location for Daily Brief weather and help other
                household areas use the same property context.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🧰" title="Home Care">
            Home Care is the actionable maintenance and repair queue. It can hold work you add directly and work
            projected from confirmed Vault maintenance, pet-care schedules, safety checks, utilities, and connected-home signals.
            <div className="hm-guide-stack">
              <MiniCard icon="📅" title="Now, upcoming, and later">
                Tasks stay organized by timing so long-range schedules do not crowd out work that needs attention now.
              </MiniCard>
              <MiniCard icon="🔧" title="Complete the work">
                Record completion, provider, cost, and notes. Where applicable, completion also updates the connected
                source record instead of leaving two conflicting maintenance histories.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🐾" title="Pets">
            Pets combines daily care with longer-term records. Profiles can hold identity, caregiver instructions,
            veterinarian contacts, insurance, health history, scheduled care, daily routines, and pet-food inventory.
            <div className="hm-guide-stack">
              <MiniCard icon="✓" title="Pet Today">
                Feeding, medication, walks, water, grooming, and other routines are completed for the current date only.
                A morning completion never marks an afternoon routine or a future weekday complete.
              </MiniCard>
              <MiniCard icon="🩺" title="Health and vet preparation">
                Keep factual observations and reviewed veterinary documents, prepare a printable visit brief, and track
                owner-entered claims without HouseMind diagnosing a condition or claiming submission to an insurer.
              </MiniCard>
              <MiniCard icon="👥" title="Controlled sharing" tone="green">
                Share one pet with a family member as View only or Help with care. Medical, veterinary, insurance, and
                document-name scopes remain separate owner choices; unrelated household data is never included.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🛟" title="Emergency & Safety">
            Emergency &amp; Safety keeps the household plan, contacts, meeting points, controls, exits, supplies,
            verification dates, and drills together. Its readiness percentage measures saved and current information;
            it is not a safety certification.
            <div className="hm-guide-stack">
              <MiniCard icon="📋" title="Guided readiness">
                Complete the compact checklist at your own pace and schedule resource checks or household drills.
                Due verification work can flow into Home Care.
              </MiniCard>
              <MiniCard icon="🚨" title="Emergency Mode">
                Open a focused, shareable or printable view with tap-to-call contacts and critical locations. HouseMind
                does not monitor emergencies or dispatch responders; contact local emergency services when needed.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="💵" title="Household Budget">
            Household Budget is the planning and comparison layer in Money. Set monthly amounts by category and compare
            them with household spending recorded by supported modules, without giving HouseMind direct bank access.
            <div className="hm-guide-stack">
              <MiniCard icon="📊" title="Plan versus actual">
                Review category totals, remaining amounts, and variance across groceries, bills, subscriptions, gifts,
                pet care, home work, and other recorded household costs.
              </MiniCard>
              <MiniCard icon="🔮" title="Budget pressure">
                Looking Ahead can surface month-end pressure when current evidence indicates a category may exceed its plan.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🗓️" title="Timeline">
            Timeline is a cross-module chronological view, not another source of records. It brings upcoming, current,
            and past household activity into one place and links each row back to the module that owns it.
            <div className="hm-guide-stack">
              <MiniCard icon="→" title="Use the source module">
                Open a Timeline row to review or change the canonical record in Food, Pets, Home Care, Safety, Events,
                Vault, Vehicles, or its other owning module.
              </MiniCard>
              <MiniCard icon="🧹" title="Compact by design">
                Repeated aggregate forecasts are collapsed, while completed and resolved history remains available for audit.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🚗" title="Vehicles">
            Vehicles tracks everything about each vehicle from purchase to service history.
            Add make, model, year, trim, VIN, color, license plate, mileage, purchase price, and a photo.
            VIN lookup is supported with a detailed trim database across most major brands.
            <div className="hm-guide-stack">
              <MiniCard icon="🔧" title="Service log">
                Log oil changes, tire rotations and replacements, brake service, transmission, battery,
                air filter, cabin filter, inspection, alignment, A/C service, recall repairs, and custom entries.
                Mileage at service is saved so HouseMind can calculate when the next interval is due.
              </MiniCard>
              <MiniCard icon="⛽" title="Fuel and repairs log">
                Separate fuel fill-up log and a repairs category for out-of-service events. Both feed into
                the vehicle intelligence summary.
              </MiniCard>
              <MiniCard icon="📋" title="Insurance and registration">
                Store insurance provider, policy number, expiry, roadside assistance coverage, and rental
                coverage. Registration expiry is tracked separately and surfaced on the dashboard before it lapses.
              </MiniCard>
              <MiniCard icon="⚠️" title="Recalls and quote check">
                Track open recalls and complaint counts. Use the quote check feature to ask whether a repair
                quote from a shop looks reasonable given your vehicle details and service history.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🔁" title="Subscriptions">
            Subscriptions tracks every recurring charge — streaming, music, software, fitness, food delivery,
            shopping, news, gaming, education, and more. Each entry includes name, provider, category,
            monthly or annual amount, billing cycle, renewal date, and optional trial end date.
            <div className="hm-guide-stack">
              <MiniCard icon="📅" title="Renewal watch">
                Renewals due within 7 days appear on the dashboard. Trial end dates are tracked separately
                and flagged before auto-renew hits.
              </MiniCard>
              <MiniCard icon="📊" title="Health and waste signals">
                HouseMind tracks usage ratings and detects low-use subscriptions. Waste signals flag services
                you're paying for but not using — fitness memberships, unused streaming tiers, and overlapping tools.
              </MiniCard>
              <MiniCard icon="✂️" title="Cancel scripts">
                When a subscription looks worth dropping, HouseMind can generate a cancellation script
                tailored to the provider with a reason drawn from your actual usage signals.
              </MiniCard>
              <MiniCard icon="💡" title="Intelligence panel">
                The subscription intelligence panel detects duplicates, suggests consolidation opportunities
                (like switching to a family plan), and calculates annual savings across your full list.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="⚡" title="Utilities">
            Utilities covers every recurring household bill: electric, gas, water, internet, phone, solar,
            trash, HOA, lawn, pest, pool, security, cable, sewer, cleaning, snow removal, gutters, and more.
            Each utility stores provider, account details, average monthly amount, and optional autopay info.
            <div className="hm-guide-stack">
              <MiniCard icon="📈" title="Bill history and spike detection">
                Add bill amounts and dates over time. HouseMind scores your bill stability and flags unusual
                increases compared to your historical average, with an explanation when AI is available.
              </MiniCard>
              <MiniCard icon="☀️" title="Solar tracking">
                Solar is a first-class utility type. Track production, credits, and net billing alongside
                your standard electric bill.
              </MiniCard>
              <MiniCard icon="📋" title="Efficiency and savings reports">
                Generate an AI efficiency report that grades your utility spending, identifies the biggest
                savings opportunities, and gives actionable recommendations. Reports are saved so you can
                compare them over time.
              </MiniCard>
              <MiniCard icon="🔗" title="Cross-module intelligence">
                Utilities connects with trips — when travel is coming up, HouseMind can flag services to
                adjust or pause. The cross-module intelligence panel inside Utilities shows insights that
                involve both billing and household activity.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🎁" title="People & Gifts">
            People &amp; Gifts keeps birthdays, interests, dislikes, budgets, gift history, registries, and
            occasions together so HouseMind can suggest thoughtful gifts without repeating what you already gave.
            <div className="hm-guide-stack">
              <MiniCard icon="👤" title="People profiles">
                Each profile can include relationship, photo, birthday, hobbies, notes, dislikes, budget,
                and gift history. Richer profiles make Ask AI answers more specific.
              </MiniCard>
              <MiniCard icon="🎁" title="Gift suggestions">
                Generate ideas filtered by the person's interests, dislikes, budget, and prior gifts.
                Saving purchased gifts prevents repeats in future suggestions.
              </MiniCard>
              <MiniCard icon="📋" title="Registries and wishlists">
                Create shareable wishlists for occasions. Guests can view and reserve items without a
                HouseMind account, and family members can suggest items for approval.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="✈️" title="Events & Trips">
            Events &amp; Trips covers travel and gatherings in one place: destination, dates, travelers,
            RSVP counts, event prep, packing, shopping, notes, public share links, and event-specific Ask AI.
            <div className="hm-guide-stack">
              <MiniCard icon="🧳" title="AI trip planner">
                Trips can generate packing lists, day-by-day plans, local tips, vehicle readiness checks,
                subscriptions worth pausing, and pantry items to use before leaving.
              </MiniCard>
              <MiniCard icon="📅" title="Event planner">
                Events track RSVP counts, prep tasks, shopping needs, pantry matches, notes, and shareable
                public links for guests.
              </MiniCard>
              <MiniCard icon="🔗" title="Cross-module trip intelligence">
                Trip dates connect with Food, Vehicles, Subscriptions, Utilities, and People &amp; Gifts so
                HouseMind can flag what needs attention before you leave.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="👥" title="Family sharing layer">
            Family sharing is a collaboration layer that lets selected household
            module items work across private accounts. Family members get separate accounts under one
            subscription and share the Family AI pool, but nothing is shared automatically.
            <div className="hm-guide-stack">
              <MiniCard icon="✅" title="Shareable modules" tone="green">
                Food, People &amp; Gifts, Events &amp; Trips, and individual pet profiles can be selectively shared.
              </MiniCard>
              <MiniCard icon="🔒" title="Always private">
                Property Profile, Emergency &amp; Safety, Household Budget, Vehicles, subscriptions, utilities, and Home Vault stay private to
                the account that owns them.
              </MiniCard>
              <MiniCard icon="📨" title="Invite flow">
                Open <Link to="/app/family" className="hm-guide-link">Family</Link>, add a member, send the
                invite, then manage shared access and activity from the Family page.
              </MiniCard>
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── ASK AI ── */}
      {tab === "ai" && (
        <div>
          <SectionCard icon="✦" title="Ask HouseMind — global">
            The <strong>✶ Ask HouseMind</strong> button on the dashboard and in the sidebar menu opens the
            global assistant with cross-module context. It can use supported pantry, property, Home Care, pet, safety,
            budget, vehicle, people, gift, trip, subscription, utility, and Vault records. Ask anything across your household —
            "What should I use from the fridge before our trip?", "Is my car service overdue?",
            "What subscriptions am I wasting money on?", "What do I still need to get for Sam's birthday?" —
            and HouseMind will draw from your actual data to answer specifically, not generically.
          </SectionCard>

          <SectionCard icon="📍" title="Module Ask AI — contextual">
            Supported modules have a fixed Ask AI bar at the bottom. These are contextual — the Food bar knows your
            current pantry tab, the Vehicles bar knows which vehicle you are looking at, the Trip bar knows your
            destination and dates. Module context is passed first; answers fall back to broader household context
            when relevant.
            <div className="hm-guide-stack">
              <MiniCard icon="🍽️" title="Food context">Knows your current tab — recipes, expiring items, meal plan, shopping, or preferences.</MiniCard>
              <MiniCard icon="🚗" title="Vehicles context">Knows the selected vehicle, service history, mileage, and insurance details.</MiniCard>
              <MiniCard icon="✈️" title="Trip context">Knows destination, dates, duration, travelers, style, budget, and transport mode.</MiniCard>
              <MiniCard icon="🔁" title="Subscriptions context">Focused on renewals, waste signals, and your full list.</MiniCard>
              <MiniCard icon="⚡" title="Utilities context">Focused on bill spikes, due dates, and savings opportunities.</MiniCard>
              <MiniCard icon="🗂️" title="Vault context">Focused on warranties, maintenance schedule, and home records.</MiniCard>
              <MiniCard icon="🐾" title="Pets context">Focused on the selected pet's saved care, routines, food, and records.</MiniCard>
              <MiniCard icon="🏡" title="Home context">Focused on Property Profile, Home Care, and saved safety information.</MiniCard>
              <MiniCard icon="💵" title="Budget context">Focused on saved monthly plans and recorded household spending.</MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="◈" title="Daily briefing">
            The daily briefing on the dashboard is an AI paragraph generated each morning from your top
            cross-module insights and attention items. It will not say everything is calm when there are real
            action items. Refresh it manually with the ↻ button after adding new data.
          </SectionCard>

          <SectionCard icon="🧠" title="Cross-module intelligence engine">
            Runs nightly and applies rules across modules to find meaningful connections. Current rules:
            food expiring during a trip, vehicle service overdue before a road trip, duplicate roadside coverage,
            subscriptions worth pausing during travel, occasions falling while you are away with no gift purchased,
            tech gifts that should be added to Vault, plus actionable connections involving pets, property, Home Care,
            safety readiness, and household budget pressure.
            Results appear on the dashboard. Refresh manually from{" "}
            <Link to="/app/settings#ai-usage" className="hm-guide-link">Settings &gt; AI usage</Link>.
          </SectionCard>

          <SectionCard icon="🎙️" title="Voice input">
            The mic button inside the Ask panel turns speech into text. The question is placed in the text box
            for review — you still press Ask to submit. Voice input never auto-submits.
          </SectionCard>

          <SectionCard icon="🚀" title="From answer to action">
            Answers include action buttons when there is a clear next step: open the right module, deep-link to
            a specific record, or go to Settings for account and support actions. Suggested follow-up prompts
            let you go deeper without retyping.
          </SectionCard>

          <SectionCard icon="⚠️" title="What AI will and will not do">
            HouseMind answers from your saved data. It will not invent records it does not have. If a pantry
            item, trip, vehicle, bill, subscription, or person profile is missing it will tell you what to add.
            It will not generate medical or legal advice, recall information, or pricing it cannot verify.
          </SectionCard>
        </div>
      )}

      {/* ── SETTINGS & SUPPORT ── */}
      {tab === "settings" && (
        <div>
          <SectionCard icon="⚙️" title="Settings sections">
            Settings is organized into: <strong>Account</strong> (name, email, password),
            <strong> Billing</strong> (current plan, upgrade, downgrade, billing cycle, cancel),
            <strong> Family accounts</strong> (invite and manage members on a Family plan),
            <strong> Privacy &amp; account</strong> (delete account),
            <strong> AI usage</strong> (track monthly requests, refresh household intelligence),
            <strong> Appearance</strong> (dark / light / system theme),
            <strong> Shopping region</strong> (helps retailer links match your location),
            <strong> AI provider</strong> (choose between OpenAI and Anthropic),
            <strong> Email reminders</strong> (how far in advance occasion reminders arrive),
            <strong> HouseMind guide</strong> (module instructions, Timeline, sharing, plans, and FAQs), and
            <strong> Support</strong> (send a message to the HouseMind team).
          </SectionCard>

          <SectionCard icon="✦" title="AI usage and intelligence refresh">
            <div className="hm-guide-stack">
              <MiniCard icon="📊" title="Track usage">
                AI usage shows how many requests you have used and how many remain this billing month.
                Usage resets monthly and does not roll over.
              </MiniCard>
              <MiniCard icon="🧠" title="Refresh household intelligence" tone="rose">
                Tap Refresh household intelligence to re-run the cross-module rules engine immediately.
                If new insights are found, HouseMind navigates to the dashboard intelligence section.
                The engine also runs automatically each night.
              </MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="🎛️" title="Appearance and preferences">
            <div className="hm-guide-stack hm-guide-stack--flush">
              <MiniCard icon="🌗" title="Theme">Light, Dark, or System to match your device setting automatically.</MiniCard>
              <MiniCard icon="🛍️" title="Shopping region">Set your region so product links resolve to the right store and currency.</MiniCard>
              <MiniCard icon="🤖" title="AI provider">Switch between OpenAI (GPT-4o) and Anthropic (Claude). Both use your household data equally.</MiniCard>
              <MiniCard icon="✉️" title="Email reminders">Control how many days before an occasion HouseMind sends a reminder. Applies to all tracked occasions.</MiniCard>
            </div>
          </SectionCard>

          <SectionCard icon="💬" title="Support">
            Go to <Link to="/app/settings#support" className="hm-guide-link">Settings &gt; Support</Link> to
            send a message to the HouseMind team. It is tied to your account email so replies come back to you.
            Ask HouseMind "how do I contact support?" and it will open the section directly.
          </SectionCard>

          <SectionCard icon="🔒" title="Billing and account management">
            All plan changes and account deletion are in Settings. Downgrades take effect at the end of the
            current billing period — you keep full access until then. Account deletion is permanent.
          </SectionCard>
        </div>
      )}

      {/* ── PLANS ── */}
      {tab === "plans" && (
        <div>
          <div className="hm-guide-plan-grid">
            {[
              {
                plan: "Free", icon: "🌱",
                desc: "Core household planning with a monthly AI allowance to get started.",
                perks: ["Personal workspace", "Household modules at starter limits", "Ask HouseMind AI (limited monthly requests)", "Email reminders", "Dashboard briefing and attention queue"],
              },
              {
                plan: "Pro", icon: "⚡",
                desc: "Full household OS with expanded module capacity and higher AI usage.",
                perks: ["Expanded limits across all modules", "Higher monthly AI requests", "Cross-module intelligence engine", "AI trip planning, event prep, efficiency reports", "Cancel scripts and subscription signals", "Lease advisor and vault intelligence"],
              },
              {
                plan: "Family", icon: "🏡",
                desc: "Multiple private accounts under one household subscription.",
                perks: ["Everything in Pro", "Up to 3 separate member accounts", "Shared Family AI request pool", "Selective sharing for Food, People & Gifts, Events & Trips, and Pets", "Family activity feed", "Independent private data per member"],
              },
            ].map(p => (
              <div key={p.plan} className="card hm-guide-plan-card">
                <div className="hm-guide-plan-card__icon">{p.icon}</div>
                <div className="hm-guide-plan-card__title">{p.plan}</div>
                <div className="hm-guide-plan-card__desc">{p.desc}</div>
                <ul>
                  {p.perks.map(k => (
                    <li key={k}>{k}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="card hm-guide-note-card">
            <p>
              Manage plan, billing, Family accounts, AI usage, and support in{" "}
              <Link to="/app/settings" className="hm-guide-link hm-guide-link--strong">Settings</Link>.
            </p>
          </div>
        </div>
      )}

      {/* ── FAQ ── */}
      {tab === "faq" && (
        <div className="card hm-guide-faq-card">
          <h3>💬 Frequently asked questions</h3>
          <p>Tap any question to expand the answer.</p>

          {GUIDE_FAQS.map(item => (
            <FaqItem key={item.question} question={item.question} answer={item.answer} />
          ))}
        </div>
      )}

      <div className="hm-guide-next">
        <div className="hm-guide-next__copy">
          <span>{isLastTab ? "Guide complete" : "Next section"}</span>
          <strong>{nextTab.icon} {nextTab.label}</strong>
        </div>
        <button
          type="button"
          className="hm-guide-next__button"
          onClick={() => selectTab(nextTab.id)}
        >
          {isLastTab ? "Back to start" : `Next: ${nextTab.label}`}
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
}
